import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import toast from "react-hot-toast";
import {
  useListGuardingEmployeesQuery,
  useListGuardingAreasQuery,
  useListSupervisorAreasQuery,
  useReplaceSupervisorAreasMutation,
} from "../guardingApi";

// Toggle-set/save pattern, same shape as BranchAvailabilityPanel.jsx
// (Product/Customer branch-mapping) - a checkbox grid of the employee's own
// home-branch Areas, explicit Save (not save-on-toggle), full-list-replace
// on submit.
const SupervisorAreaDetail = () => {
  const { employeeRecordGuid } = useParams();

  const { data: employees = [] } = useListGuardingEmployeesQuery({});
  const employee = employees.find((e) => String(e.recordGuid) === String(employeeRecordGuid));

  const { data: areas = [], isLoading: isLoadingAreas } = useListGuardingAreasQuery(
    { branchId: employee?.branchId },
    { skip: !employee }
  );
  const { data: assigned = [] } = useListSupervisorAreasQuery(
    { employeeRecordGuid },
    { skip: !employeeRecordGuid }
  );
  const [replaceAreas, { isLoading: isSaving }] = useReplaceSupervisorAreasMutation();

  const [selected, setSelected] = useState(new Set());
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setSelected(new Set(assigned.map((a) => a.areaId)));
    setDirty(false);
  }, [assigned, employeeRecordGuid]);

  if (!employee) {
    return <div className="p-4 text-muted">Employee not found.</div>;
  }

  const toggle = (areaId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(areaId)) next.delete(areaId);
      else next.add(areaId);
      return next;
    });
    setDirty(true);
  };

  const handleSave = async () => {
    try {
      await replaceAreas({
        employeeRecordGuid,
        branchId: employee.branchId,
        areaIds: [...selected],
      }).unwrap();
      toast.success("Supervisor's areas updated");
      setDirty(false);
    } catch (error) {
      toast.error(error?.message || "Could not update supervisor areas");
    }
  };

  return (
    <div className="p-4">
      <h4 className="mb-1">{employee.empName} {employee.empLastName}</h4>
      <p className="text-muted mb-4">
        {employee.empCode} &middot; {employee.branchCode}{employee.designationName ? ` · ${employee.designationName}` : ""}
      </p>

      <div className="card">
        <div className="card-body">
          <h5 className="mb-2">Assigned Areas</h5>
          <p className="text-muted small mb-3">
            Tick every Area this employee supervises - the same Area can be assigned to more than one supervisor.
          </p>
          {isLoadingAreas ? (
            <p className="text-muted mb-0">Loading...</p>
          ) : (
            <>
              <div className="row mb-3">
                {areas.map((a) => (
                  <div className="col-md-4 mb-2" key={a.areaId}>
                    <div className="form-check form-switch">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id={`supervisor-area-${a.areaId}`}
                        style={{ width: "2.5em", height: "1.4em" }}
                        checked={selected.has(a.areaId)}
                        onChange={() => toggle(a.areaId)}
                      />
                      <label className="form-check-label ms-2" htmlFor={`supervisor-area-${a.areaId}`}>
                        {a.areaName}
                      </label>
                    </div>
                  </div>
                ))}
              </div>
              <button type="button" className="btn btn-primary btn-sm" onClick={handleSave} disabled={!dirty || isSaving}>
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupervisorAreaDetail;
